import { useEffect, useContext } from 'react';
import * as d3 from 'd3';
import { InterfaceContext } from '../store/InterfaceProvider';
import { wrapText } from '../utils/svgTextWrap';

const config = {
    ed_visits: {
        rate: {
            yAxisLabel: 'Rate per 100,000 ED visits',
            tooltipText: 'Rate per 100k ED Visits',
            title: {
                bar: {
                    compare: 'Firearm Injury ED Visit Rate by Geography,',
                    time: 'Firearm Injury ED Visit Rate by Year,'
                },
                line: 'Firearm Injury ED Visit Rate by Month,'
            }
        }
    },
    deaths: {
        rate: {
            yAxisLabel: 'Rate per 100,000 persons',
            tooltipText: 'Rate per 100k',
            //endpoints 3
            fa_deaths: {
                title: {
                    bar: {
                        compare: 'All Firearm Injury Death Rate by Geography,',
                        time: 'All Firearm Injury Death Rate by Year,'
                    },
                    line: 'All Firearm Injury Death Rate by Month,'
                }
            },
            all_homicide: {
                title: {
                    bar: {
                        compare: 'Homicide Rate by Geography,',
                        time: 'Homicide Rate by Year,'
                    },
                    line: 'Homicide Rate by Month,'
                }
            },
            all_suicide: {
                title: {
                    bar: {
                        compare: 'Suicide Rate by Geography,',
                        time: 'Suicide Rate by Year,'
                    },
                    line: 'Suicide Rate by Month,'
                }
            },
            fa_homicide: {
                title: {
                    bar: {
                        compare: 'Firearm Homicide Rate by Geography,',
                        time: 'Firearm Homicide Rate by Year,'
                    },
                    line: 'Firearm Homicide Rate by Month,'
                }
            },
            fa_suicide: {
                title: {
                    bar: {
                        compare: 'Firearm Suicide Rate by Geography,',
                        time: 'Firearm Suicide Rate by Year,'
                    },
                    line: 'Firearm Suicide Rate by Month,'
                }
            },
            drug_od: {
                title: {
                    bar: {
                        compare: 'Unintentional Drug Overdose Death Rate by Geography,',
                        time: 'Unintentional Drug Overdose Death Rate by Year,'
                    },
                    line: 'Unintentional Drug Overdose Death Rate by Month,'
                }
            },
            //endpoints 2
            all: {
                title: {
                    bar: {
                        compare: 'All Firearm Injury Death Rate by Geography,',
                        time: 'All Firearm Injury Death Rate by Year,'
                    },
                    line: 'All Firearm Injury Death Rate by Month,'
                }
            },
            homicide: {
                title: {
                    bar: {
                        compare: 'Firearm Homicide Rate by Geography,',
                        time: 'Firearm Homicide Rate by Year,'
                    },
                    line: 'Firearm Homicide Rate by Month,'
                }
            },
            suicide: {
                title: {
                    bar: {
                        compare: 'Firearm Suicide Rate by Geography,',
                        time: 'Firearm Suicide Rate by Year,'
                    },
                    line: 'Firearm Suicide Rate by Month,'
                }
            }
        },
        count: {
            yAxisLabel: 'Number of Deaths',
            tooltipText: 'Number of Deaths',
            //endpoints 3
            fa_deaths: {
                title: {
                    line: 'Number of All Firearm Injury Deaths by Month,'
                }
            },
            all_homicide: {
                title: {
                    line: 'Number of Homicides by Month,'
                }
            },
            all_suicide: {
                title: {
                    line: 'Number of Suicides by Month,'
                }
            },
            fa_homicide: {
                title: {
                    line: 'Number of Firearm Homicides by Month,'
                }
            },
            fa_suicide: {
                title: {
                    line: 'Number of Firearm Suicide by Month,'
                }
            },
            drug_od: {
                title: {
                    line: 'Number of Unintentional Drug Overdose Deaths by Month,'
                }
            },

            //endpoints 2
            all: {
                title: {
                    line: 'Number of All Firearm Injury Deaths by Month,'
                }
            },
            homicide: {
                title: {
                    line: 'Number of Firearm Homicides by Month,'
                }
            },
            suicide: {
                title: {
                    line: 'Number of Firearm Suicides by Month,'
                }
            }
        }
    }
};

const useResizeChart = (svgRef, data, dimensions, chartType, isHorizontal, scale, selection, view, measure, deathType, period) => {
    const { past12Months, activePast12Months } = useContext(InterfaceContext);

    const isReset = scale === 'county' && selection?.GEOID.length > 5 ? true : false;
    const hasNoData = data.some((obj) => obj.value === -888);
    const hasUnstableData = data.some((obj) => obj.value === -999);
    const hasModeledData = data.some((obj) => obj.modeled === true && obj.value !== -999);
    const formatName = (string) => {
        if (!string?.includes(',')) return string;
        const appendedGroupScale = {
            zcta: 'ZIP Codes',
            census: 'Census Tracts'
        };
        const strArray = string.split(', ');
        return `${strArray.length} Grouped ${appendedGroupScale[scale]}`;
    };
    const getChartTitle = (chartType, view, measure, deathType, selection, period) => {
        let title = '';
        let geography = formatName(selection?.name);
        let timePeriod = period === 'ttm' ? activePast12Months : period;

        if (chartType === 'line') {
            if (view === 'ed_visits') {
                title = config.ed_visits.rate.title.line;
            } else if (selection.GEOID === '1' && measure === 'count') {
                title = config.deaths.count[deathType].title.line;
            } else {
                title = config.deaths.rate[deathType].title.line;
            }
        } else {
            if (view === 'ed_visits') {
                title = isHorizontal ? config.ed_visits.rate.title.bar.compare : config.ed_visits.rate.title.bar.time;
            } else {
                title = isHorizontal ? config.deaths.rate[deathType].title.bar.compare : config.deaths.rate[deathType].title.bar.time;
            }
        }
        return chartType === 'line' || !isHorizontal ? `${title} ${geography}` : `${title} ${timePeriod}`;
    };

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 70, right: 70, bottom: 90, left: 70 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        const tooltip = d3.select('body').append('div').attr('class', 'tooltip').style('position', 'absolute').style('background', '#f6fbff').style('border', '2px solid #59c0d7').style('padding', '10px').style('border-radius', '7px').style('display', 'none').style('z-index', '10').style('pointer-events', 'none');

        let x = d3.scaleBand().rangeRound([0, width]).padding(0.5);
        let y = d3.scaleLinear().rangeRound([height, 0]);

        const parseDate = d3.timeParse('%Y-%m-%d');
        const labelFormatSpecifier = chartType === 'bar' ? '%Y' : "%b '%y";
        const formatDate = d3.timeFormat(labelFormatSpecifier);

        const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

        const title = svg
            .append('text')
            .attr('class', 'chart-title')
            .attr('x', width / 2)
            .attr('y', 40)
            .attr('text-anchor', 'middle')
            .style('font-size', '18px')
            .style('font-weight', 'bold')
            .text(getChartTitle(chartType, view, measure, deathType, selection, period));

        wrapText(title, width + margin.right + margin.left);

        if (chartType === 'line') {
            x = hasNoData ? d3.scaleTime().range([0, width]) : d3.scaleTime().range([40, width - 40]);
            y = d3.scaleLinear().rangeRound([height, 0]);
            //increase max y value by 20 percent and round up to increase y axis
            const maxY = Math.ceil(d3.max(data, (d) => d.value) + d3.max(data, (d) => d.value) * 0.2);
            x.domain(d3.extent(data, (d) => parseDate(d.date)));
            y.domain([0, maxY]);
            const ticksCount = d3.timeMonth.every(6);
            const yAxisGridLine = d3.axisLeft(y).tickSize(-width).tickFormat('');

            if (hasNoData) {
                g.append('g').attr('class', 'y axis').call(d3.axisLeft(y).tickSize(0)).selectAll('text').remove();
                g.append('g').attr('class', 'x axis').attr('transform', `translate(0, ${height})`).call(d3.axisBottom(x).ticks(0)).selectAll('text').remove();
                g.append('text')
                    .text('No data available.')
                    .attr('font-size', '2em')
                    .attr('text-anchor', 'middle')
                    .attr('alignment-baseline', 'middle')
                    .attr('transform', `translate(${width / 2}, ${height / 2})`)
                    .attr('class', 'no-data-message');
            } else {
                const handleSuppresedData = (data) => {
                    if (isNaN(data) || data < 0) {
                        return false;
                    } else {
                        return true;
                    }
                };

                const line = d3
                    .line()
                    .x((d) => x(parseDate(d.date)))
                    .y((d) => y(d.value))
                    .defined((d) => handleSuppresedData(d.value));

                g.append('g').attr('class', 'x axis').attr('transform', `translate(0, ${height})`).call(d3.axisBottom(x).tickFormat(formatDate).ticks(ticksCount).tickSizeOuter(0)).selectAll('line, path').attr('stroke', '#D3D3D3').attr('fill', 'none');

                g.append('g').attr('class', 'y axis').call(d3.axisLeft(y)).selectAll('line, path').attr('stroke', '#D3D3D3').attr('fill', 'none');

                g.append('g').attr('class', 'y grid').call(yAxisGridLine).selectAll('line').attr('stroke', '#D3D3D3');

                g.append('path').datum(data).attr('class', 'line').attr('d', line).attr('stroke', '#4C8DF6').attr('stroke-width', 4).attr('fill', 'none');

                const filteredData = data.filter((obj) => obj.value !== -999);

                g.selectAll('.dot')
                    .data(filteredData)
                    .enter()
                    .append('circle')
                    .attr('class', 'dot')
                    .attr('cx', (d) => x(parseDate(d.date)))
                    .attr('cy', (d) => y(d.value))
                    .attr('r', 4)
                    .attr('fill', '#4C8DF6')
                    .on('mouseover', (evt, d) => {
                        tooltip
                            .style('display', 'block')
                            .html(`<strong>Date: ${isReset || selection.GEOID === '1' ? formatDate(parseDate(d.date)) : d.date.slice(0, 4)}</strong><br>${selection.GEOID === '1' ? config[view][measure].tooltipText : d.modeled ? `Modeled ${config[view]['rate'].tooltipText}` : config[view]['rate'].tooltipText}: ${Number.isInteger(d.value) ? d.value : d.value.toFixed(1)}`)
                            .style('left', evt.pageX - 60 + 'px')
                            .style('top', evt.pageY - 75 + 'px');
                    })
                    .on('mouseout', () => tooltip.style('display', 'none'));
            }

            svg.append('text')
                .attr('class', 'y axis-label')
                .attr('text-anchor', 'middle')
                .attr('transform', `translate(${margin.left / 4}, ${height / 2 + margin.top}) rotate(-90)`)
                .text(`${selection.GEOID === '1' ? config[view][measure].yAxisLabel : config[view]['rate'].yAxisLabel}`);

            if (hasUnstableData) {
                const unstableDataText = svg
                    .append('text')
                    .attr('class', 'x axis-label')
                    .attr('x', width)
                    .attr('y', height + 110)
                    .text('Data is not shown when a stable rate cannot be calculated due to small numbers.')
                    .attr('font-size', '14px')
                    .attr('font-style', 'italic');

                wrapText(unstableDataText, width + margin.right + margin.left);
            }

            if (data.length > 5 && dimensions.width < 600) {
                svg.selectAll('.tick text').attr('transform', 'rotate(-45)').style('text-anchor', 'end').attr('dx', '-.8em').attr('dy', '.15em');
            }

            svg.selectAll('.domain').attr('stroke', '#D3D3D3');
        } else if (chartType === 'bar') {
            let barColors = scale === 'usstate' || scale === 'county' ? ['#4C8DF6', '#7BCCC4', '#02818A'] : ['#4C8DF6', '#7BCCC4', '#A6BDDB'];
            const getColor = (scale) => {
                if (selection.GEOID === '1') return '#4C8DF6';
                let color = '';
                switch (scale) {
                    case 'usstate':
                        color = '#7BCCC4';
                        break;
                    case 'county':
                        color = isReset ? '#4C8DF6' : '#02818A';
                        break;
                    case 'census':
                        color = '#A6BDDB';
                        break;
                    case 'zcta':
                        color = '#A6BDDB';
                        break;
                    default:
                        color = '#4C8DF6';
                }
                return color;
            };
            if (!isHorizontal && hasNoData) {
                g.append('g').attr('class', 'y axis').call(d3.axisLeft(y).tickSize(0)).selectAll('text').remove();
                g.append('g').attr('class', 'x axis').attr('transform', `translate(0, ${height})`).call(d3.axisBottom(x).ticks(0)).selectAll('text').remove();
                g.append('text')
                    .text('No data available.')
                    .attr('font-size', '2em')
                    .attr('text-anchor', 'middle')
                    .attr('alignment-baseline', 'middle')
                    .attr('transform', `translate(${width / 2}, ${height / 2})`)
                    .attr('class', 'no-data-message');
            } else {
                //increase max y value by 20 percent and round up to increase y axis
                const max = Math.ceil(d3.max(data, (d) => d.value) + d3.max(data, (d) => d.value) * 0.2);
                if (isHorizontal) {
                    x = d3.scaleLinear().range([0, width]);
                    y = d3.scaleBand().rangeRound([0, height]).padding(0.5);
                    x.domain([0, max]);
                    y.domain(data.map((d) => d.name));
                } else {
                    x = d3.scaleBand().rangeRound([0, width]).padding(0.5);
                    y = d3.scaleLinear().rangeRound([height, 0]);
                    x.domain(data.map((d) => parseDate(d.date)));
                    if (max <= 0 || isNaN(max)) {
                        y.domain([0, 10]);
                    } else {
                        y.domain([0, max]);
                    }
                }
                const tickWidth = 50;
                const xAxisGrid = d3
                    .axisBottom(x)
                    .tickSize(-height)
                    .ticks(width / tickWidth)
                    .tickFormat('');
                const yAxisGridBar = d3.axisLeft(y).tickSize(-width).tickFormat('');

                g.append('g')
                    .attr('class', 'x-axis')
                    .attr('transform', `translate(0, ${height})`)
                    .call(isHorizontal ? d3.axisBottom(x).ticks(width / tickWidth) : d3.axisBottom(x).tickFormat(formatDate))
                    .selectAll('line, path')
                    .attr('stroke', '#D3D3D3')
                    .attr('fill', 'none');

                g.append('g').attr('class', 'y-axis').call(d3.axisLeft(y)).selectAll('line, path').attr('stroke', '#D3D3D3').attr('fill', 'none');

                g.append('g')
                    .attr('class', 'x grid')
                    .attr('transform', `translate(0, ${isHorizontal ? height : 0})`)
                    .call(isHorizontal ? xAxisGrid : yAxisGridBar)
                    .selectAll('line')
                    .attr('stroke', '#D3D3D3');

                const bars = g.selectAll('.bar').data(data).enter().append('g').attr('class', 'bar-group');

                const getTooltipValue = (value) => {
                    let label = '';
                    switch (value) {
                        case -888:
                            label = 'N/A';
                            break;
                        case -999:
                            label = 'Unstable Rate';
                            break;
                        default:
                            label = value.toFixed(1);
                    }
                    return label;
                };

                bars.append('rect')
                    .attr('class', 'bar')
                    .attr('x', (d) => (isHorizontal ? 0 : x(parseDate(d.date))))
                    .attr('y', (d) => (isHorizontal ? y(d.name) : d.value > 0 ? y(d.value) : height - 5.5))
                    .attr('height', (d) => {
                        if (isHorizontal) {
                            return y.bandwidth();
                        } else {
                            return Math.max(0, d.value > 0 ? height - y(d.value) : 15);
                        }
                    })
                    .attr('width', (d) => (!isHorizontal ? x.bandwidth() : d.value > 0 ? x(d.value) : 5))
                    .attr('fill', (d, i) => (isHorizontal ? barColors[i] : getColor(scale)))
                    .on('mouseover', (evt, d) => {
                        tooltip
                            .style('display', 'block')
                            .html(`<strong>${formatName(d.name)}</strong><br>${d.modeled ? `Modeled ${config[view]['rate'].tooltipText}` : config[view]['rate'].tooltipText}: ${getTooltipValue(d.value)}`)
                            .style('left', evt.pageX - 60 + 'px')
                            .style('top', evt.pageY - 75 + 'px');
                    })
                    .on('mouseout', () => tooltip.style('display', 'none'));

                let maxHeight = 0;

                if (isHorizontal) {
                    bars.each(function (d, i) {
                        let bar = d3.select(this);
                        let barHeight = bar.attr('height');
                        if (barHeight > maxHeight) {
                            maxHeight = barHeight;
                        }
                    })
                        .filter((d) => d.value <= 0)
                        .attr('height', maxHeight);

                    const labels = bars.append('g');

                    labels
                        .append('rect')
                        .attr('x', 5)
                        .attr('y', (d) => y(d.name) - 27)
                        .attr('height', '1.5em')
                        .attr('width', (d) => d.name.length * 8) // approximate width based on character count
                        .attr('fill', 'white');

                    labels
                        .append('text')
                        .attr('x', 10)
                        .attr('y', (d) => y(d.name) - 15)
                        .attr('dy', '.35em')
                        .text((d) => d.name);
                } else {
                    svg.selectAll('.bar')
                        .filter((d) => d.value <= 0)
                        .attr('height', 5)
                        .attr('y', height - 5);
                }

                const getBarLabel = (value) => {
                    let label = '';
                    switch (value) {
                        case 0:
                            label = '0.0';
                            break;
                        case -888:
                            label = 'N/A';
                            break;
                        case -999:
                            label = '*';
                            break;
                        default:
                            label = value.toFixed(1);
                    }
                    return label;
                };

                bars.each(function (d) {
                    const g = d3.select(this).append('g');
                    const getOffset = (modeled) => (modeled ? 27 : 25);
                    const text = g
                        .append('text')
                        .attr('x', (d) => {
                            if (isHorizontal) {
                                return d.value > 0 ? x(d.value) + getOffset(d.modeled) : 25;
                            } else {
                                let baseValue = x(parseDate(d.date)) + x.bandwidth() / 2;
                                if (d.value > 0) {
                                    return baseValue;
                                } else if (d.value === 0) {
                                    return baseValue;
                                } else {
                                    return baseValue;
                                }
                            }
                        })
                        .attr('y', (d) => {
                            if (isHorizontal) {
                                return d.value > 0 ? y(d.name) + y.bandwidth() / 2 + 6 : y(d.name) + y.bandwidth() / 2 + 5;
                            } else if (d.value <= 0) {
                                return d.value === 0 ? height - 14 : height - 10;
                            } else {
                                return y(d.value) - 10;
                            }
                        })
                        .attr('text-anchor', 'middle')
                        .style('font-weight', 'bold')
                        .text((d) => getBarLabel(d.value))
                        .each(function (d) {
                            if (d?.modeled && d?.value > 0) {
                                d3.select(this).append('tspan').attr('baseline-shift', 'super').text('†');
                            }
                        });

                    text.each(function () {
                        const bbox = this.getBBox();

                        g.insert('rect', 'text')
                            .attr('x', bbox.x)
                            .attr('y', bbox.y + 2)
                            .attr('width', bbox.width)
                            .attr('height', bbox.height - 5)
                            .style('fill', 'white');
                    });
                });

                svg.append('text')
                    .attr('class', isHorizontal ? 'x axis-label' : 'y axis-label')
                    .attr('text-anchor', 'middle')
                    .attr('transform', isHorizontal ? `translate(${width / 2 + margin.left}, ${height + margin.bottom + 20})` : `translate(${margin.left - 40}, ${height / 2 + margin.top}) rotate(-90)`)
                    .text(config[view]['rate'].yAxisLabel);

                svg.selectAll('.tick text').attr('font-size', '14px');

                svg.selectAll('.domain').attr('stroke', '#D3D3D3');

                if (isHorizontal) svg.selectAll('.y-axis .tick').remove();

                if (hasUnstableData) {
                    const unstableDataText = svg
                        .append('text')
                        .attr('class', 'x axis-label')
                        .attr('y', isHorizontal ? (width <= 460 ? height + 140 : height + 135) : width <= 460 ? height + 120 : height + 135)
                        .text('* Data is not shown when a stable rate cannot be calculated due to small numbers.')
                        .attr('font-size', '14px')
                        .attr('font-style', 'italic');

                    wrapText(unstableDataText, width + margin.right + margin.left);
                }

                if (hasModeledData) {
                    const modeledDataText = svg
                        .append('text')
                        .attr('class', 'x axis-label')
                        .attr('y', height + 150)
                        .text('† Modeled rate.')
                        .attr('font-size', '14px')
                        .attr('font-style', 'italic');

                    wrapText(modeledDataText, width + margin.right + margin.left);
                }
            }
        }

        return () => {
            tooltip.remove();
        };
    }, [svgRef, data, dimensions, chartType, selection, view, measure, deathType, period]);
};

export default useResizeChart;
